import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  TextInput,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../theme';
import { apiService } from '../services/api';
import { DatePickerField } from './DatePickerField';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

const MOBILE_HEADER_OFFSET = 72;
const DEFAULT_DURATION_MS = 15 * 60 * 1000;

interface CalendarOption {
  _id: string;
  name: string;
  isDefault?: boolean;
}

interface QuickReminderModalProps {
  visible: boolean;
  onClose: () => void;
  contactName: string;
  contactPhoneNumber?: string;
  onCreated?: () => void;
  onNotify: (type: 'success' | 'error', title: string, message: string) => void;
}

/**
 * Lightweight "add a reminder" sheet used from the post-call quick actions —
 * creates a real CalendarEvent (there's no separate reminder concept in the
 * backend) on a calendar the user picks. Deliberately no default-calendar
 * auto-selection: getCalendars already sorts isDefault first, so it's simply
 * the top of the list rather than something silently pre-chosen.
 */
export const QuickReminderModal: React.FC<QuickReminderModalProps> = ({
  visible,
  onClose,
  contactName,
  contactPhoneNumber,
  onCreated,
  onNotify,
}) => {
  const styles = useThemedStyles(getStyles);
  const [calendars, setCalendars] = useState<CalendarOption[]>([]);
  const [loadingCalendars, setLoadingCalendars] = useState(false);
  const [calendarId, setCalendarId] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setTitle(`Follow up with ${contactName}`);
    setNote('');
    setStartDate(new Date(Date.now() + 60 * 60 * 1000)); // default: 1 hour from now
    setCalendarId(undefined);
    loadCalendars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const loadCalendars = async () => {
    setLoadingCalendars(true);
    try {
      const businessId = apiService.getBusinessIdFromToken();
      const res = await apiService.getCalendars({ businessId: businessId || undefined, isActive: true });
      if (res.statusCode === 1000 && Array.isArray(res.data)) {
        const options: CalendarOption[] = res.data.map((c: any) => ({ _id: c._id, name: c.name, isDefault: c.isDefault }));
        setCalendars(options);
        if (options.length > 0) setCalendarId(options[0]._id);
      }
    } catch (err) {
      console.error('[QuickReminderModal] Failed to load calendars:', err);
    } finally {
      setLoadingCalendars(false);
    }
  };

  const handleSubmit = async () => {
    if (!calendarId || !title.trim() || !startDate) return;
    setSubmitting(true);
    try {
      const businessId = apiService.getBusinessIdFromToken();
      if (!businessId) throw new Error('No business context');
      const endDate = new Date(startDate.getTime() + DEFAULT_DURATION_MS);
      const description = [
        note.trim() || undefined,
        contactPhoneNumber ? `Re: call with ${contactName} (${contactPhoneNumber})` : `Re: call with ${contactName}`,
      ].filter(Boolean).join('\n\n');
      const res = await apiService.createEvent({
        calendarId,
        businessId,
        title: title.trim(),
        description,
        startDate,
        endDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      if (res.statusCode === 1000) {
        onNotify('success', 'Reminder added', `Added to your calendar for ${startDate.toLocaleString()}`);
        onCreated?.();
        onClose();
      } else {
        onNotify('error', 'Could not add reminder', res.errors?.[0]?.message || 'Please try again.');
      }
    } catch (err: any) {
      console.error('[QuickReminderModal] Failed to create reminder:', err);
      onNotify('error', 'Could not add reminder', err?.message || 'Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !!calendarId && !!title.trim() && !!startDate && !submitting;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={onClose}>
        <TouchableOpacity style={styles.modalContainer} activeOpacity={1} onPress={(e) => e.stopPropagation()}>
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Add Reminder</Text>
              <Text style={styles.subtitle}>{contactName}</Text>
            </View>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color={colors.text.primary as string} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content}>
            <Text style={styles.label}>Calendar</Text>
            {loadingCalendars ? (
              <View style={styles.loadingInline}>
                <ActivityIndicator size="small" color={colors.primary as string} />
                <Text style={styles.loadingInlineText}>Loading calendars…</Text>
              </View>
            ) : calendars.length === 0 ? (
              <Text style={styles.emptyText}>No calendars found — create one first.</Text>
            ) : (
              <View style={styles.calendarList}>
                {calendars.map((c) => (
                  <TouchableOpacity
                    key={c._id}
                    style={[styles.calendarOption, calendarId === c._id && styles.calendarOptionActive]}
                    onPress={() => setCalendarId(c._id)}
                  >
                    <Ionicons
                      name={calendarId === c._id ? 'radio-button-on' : 'radio-button-off'}
                      size={18}
                      color={calendarId === c._id ? (colors.primary as string) : (colors.text.secondary as string)}
                    />
                    <Text style={styles.calendarOptionText}>{c.name}</Text>
                    {c.isDefault && <Text style={styles.defaultBadge}>Default</Text>}
                  </TouchableOpacity>
                ))}
              </View>
            )}

            <View style={styles.field}>
              <Text style={styles.label}>Title</Text>
              <TextInput
                style={styles.input}
                value={title}
                onChangeText={setTitle}
                placeholder="Reminder title"
                placeholderTextColor={colors.text.disabled as string}
              />
            </View>

            <View style={styles.field}>
              <DatePickerField label="When" value={startDate} onChange={setStartDate} mode="datetime" minimumDate={new Date()} />
            </View>

            <View style={styles.field}>
              <Text style={styles.label}>Note (optional)</Text>
              <TextInput
                style={[styles.input, styles.noteInput]}
                value={note}
                onChangeText={setNote}
                placeholder="Add a note…"
                placeholderTextColor={colors.text.disabled as string}
                multiline
              />
            </View>

            <TouchableOpacity
              style={[styles.submitButton, !canSubmit && styles.submitButtonDisabled]}
              onPress={handleSubmit}
              disabled={!canSubmit}
              activeOpacity={0.85}
            >
              {submitting ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.submitButtonText}>Add Reminder</Text>
              )}
            </TouchableOpacity>
          </ScrollView>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: Platform.OS === 'web' ? 'center' : 'flex-start',
    alignItems: Platform.OS === 'web' ? 'center' : 'stretch',
    padding: Platform.OS === 'web' ? spacing.lg : 0,
    paddingTop: Platform.OS === 'web' ? spacing.lg : 0,
    ...(Platform.OS !== 'web' && {
      position: 'absolute',
      top: MOBILE_HEADER_OFFSET,
      left: 0,
      right: 0,
      bottom: 0,
    }),
  },
  modalContainer: {
    backgroundColor: colors.background.surface,
    borderRadius: Platform.OS === 'web' ? 16 : 0,
    width: '100%',
    maxWidth: Platform.OS === 'web' ? 480 : undefined,
    maxHeight: Platform.OS === 'web' ? '85%' : undefined,
    flex: Platform.OS === 'web' ? undefined : 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border as string,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold' as const,
    color: colors.text.primary as string,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary as string,
  },
  closeButton: { padding: spacing.xs },
  content: { padding: spacing.lg },
  label: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600' as const,
    color: colors.text.secondary as string,
    marginBottom: spacing.xs,
  },
  loadingInline: { flexDirection: 'row', alignItems: 'center', marginBottom: spacing.md, gap: spacing.sm },
  loadingInlineText: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string },
  emptyText: { fontSize: typography.fontSize.sm, color: colors.text.secondary as string, marginBottom: spacing.md },
  calendarList: { marginBottom: spacing.md, gap: spacing.xs },
  calendarOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.border as string,
    backgroundColor: colors.background.secondary as string,
  },
  calendarOptionActive: {
    borderColor: colors.primary as string,
    backgroundColor: withAlpha(colors.primary, 0.06),
  },
  calendarOptionText: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary as string },
  defaultBadge: {
    fontSize: typography.fontSize.xs,
    color: colors.primary as string,
    fontWeight: '600' as const,
  },
  field: { marginBottom: spacing.md },
  input: {
    borderWidth: 1,
    borderColor: colors.border as string,
    borderRadius: 8,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    fontSize: typography.fontSize.base,
    color: colors.text.primary as string,
    backgroundColor: colors.background.surface as string,
  },
  noteInput: { minHeight: 72, textAlignVertical: 'top' as const },
  submitButton: {
    backgroundColor: colors.primary as string,
    borderRadius: 14,
    paddingVertical: spacing.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.lg,
  },
  submitButtonDisabled: { opacity: 0.5 },
  submitButtonText: { color: '#FFFFFF', fontSize: typography.fontSize.base, fontWeight: 'bold' as const },
});
